import { useEffect, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import {
  createMenuItem,
  deleteMenuItem,
  fetchMenuCategories,
  fetchMenuItems,
  uploadMenuItemImage,
  updateMenuItem,
  updateMenuItemAvailability,
  type MenuCategory,
  type MenuItem,
} from '../../lib/api';

export default function MenuPage() {
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    menuCategoryId: '',
    price: '',
    description: '',
    imageUrl: '',
    available: true
  });
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('');
  const [removeCurrentImage, setRemoveCurrentImage] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      try {
        setIsLoading(true);
        const [categoriesResponse, itemsResponse] = await Promise.all([
          fetchMenuCategories(),
          fetchMenuItems(true),
        ]);

        if (!isMounted) {
          return;
        }

        setCategories(categoriesResponse.categories);
        setMenuItems(itemsResponse.items);
        setError(null);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        setError(loadError instanceof Error ? loadError.message : 'Unable to load menu.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  const resetForm = () => {
    if (imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setFormData({
      name: '',
      menuCategoryId: String(categories[0]?.id ?? ''),
      price: '',
      description: '',
      imageUrl: '',
      available: true
    });
    setSelectedImageFile(null);
    setImagePreviewUrl('');
    setRemoveCurrentImage(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let imageUrl: string | null = removeCurrentImage ? null : (formData.imageUrl || null);

      if (selectedImageFile) {
        const uploadResponse = await uploadMenuItemImage(selectedImageFile);
        imageUrl = uploadResponse.imageUrl;
      }

      const payload = {
        menuCategoryId: Number(formData.menuCategoryId),
        itemName: formData.name,
        description: formData.description,
        imageUrl,
        basePrice: Number(formData.price),
        isAvailable: formData.available,
      };

      if (editingItem) {
        const response = await updateMenuItem({
          menuItemId: editingItem.id,
          ...payload,
        });

        setMenuItems(menuItems.map(item => item.id === editingItem.id ? response.item : item));
      } else {
        const response = await createMenuItem(payload);
        setMenuItems([...menuItems, response.item]);
      }

      setShowAddModal(false);
      setEditingItem(null);
      resetForm();
      setError(null);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Unable to save menu item.');
    }
  };

  const handleEdit = (item: MenuItem) => {
    if (imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setEditingItem(item);
    setFormData({
      name: item.name,
      menuCategoryId: String(item.menuCategoryId),
      price: String(item.price),
      description: item.description ?? '',
      imageUrl: item.image ?? '',
      available: item.available
    });
    setSelectedImageFile(null);
    setImagePreviewUrl(item.image ?? '');
    setRemoveCurrentImage(false);
    setShowAddModal(true);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    if (imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setSelectedImageFile(file);
    setRemoveCurrentImage(false);

    if (file) {
      setImagePreviewUrl(URL.createObjectURL(file));
      return;
    }

    setImagePreviewUrl(formData.imageUrl);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await deleteMenuItem(id);
      setMenuItems(menuItems.filter(item => item.id !== id));
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Unable to delete menu item.');
    }
  };

  const toggleAvailability = async (id: number) => {
    const item = menuItems.find(menuItem => menuItem.id === id);

    if (!item) {
      return;
    }

    try {
      const response = await updateMenuItemAvailability(id, !item.available);
      setMenuItems(menuItems.map(menuItem => menuItem.id === id ? response.item : menuItem));
    } catch (toggleError) {
      setError(toggleError instanceof Error ? toggleError.message : 'Unable to update availability.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">Menu Management</h1>
            <p className="text-sm text-gray-600">Add, edit, and manage your menu items</p>
          </div>
          <button
            onClick={() => {
              setEditingItem(null);
              resetForm();
              setShowAddModal(true);
            }}
            className="bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors flex items-center space-x-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line text-xl w-5 h-5 flex items-center justify-center"></i>
            <span>Add New Item</span>
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-4">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center text-sm text-gray-500">
            Loading menu items...
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {menuItems.map(item => (
              <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="relative w-full h-48 bg-gray-100">
                {item.image ? (
                  <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-contain object-center bg-gray-100"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 px-4 text-center">
                      <span className="text-lg font-semibold text-gray-600">{item.name}</span>
                    </div>
                  )}
                  <div className="absolute top-2 right-2">
                    <button
                      onClick={() => toggleAvailability(item.id)}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${
                        item.available
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-500 text-white'
                      } whitespace-nowrap cursor-pointer`}
                    >
                      {item.available ? 'Available' : 'Unavailable'}
                    </button>
                  </div>
                </div>

                <div className="p-4">
                  <div className="mb-3">
                    <h3 className="font-semibold text-base text-gray-900 mb-1">{item.name}</h3>
                    <p className="text-xs text-gray-500 mb-2">{item.category}</p>
                    <p className="text-sm text-gray-600 line-clamp-2">{item.description ?? 'No description provided.'}</p>
                  </div>

                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xl font-bold text-teal-600">KSh {item.price.toLocaleString()}</span>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(item)}
                      className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center justify-center space-x-1 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-edit-line w-4 h-4 flex items-center justify-center"></i>
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="flex-1 bg-red-50 text-red-600 py-2 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors flex items-center justify-center space-x-1 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingItem ? 'Edit Menu Item' : 'Add New Menu Item'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setEditingItem(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Item Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter item name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.menuCategoryId}
                    onChange={(e) => setFormData({ ...formData, menuCategoryId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                    required
                  >
                    <option value="" disabled>Select a category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price (KSh) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="Enter price"
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Item Image
                  </label>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleImageChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm file:mr-3 file:rounded-md file:border-0 file:bg-teal-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-teal-700"
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Optional. Upload JPG, PNG, WEBP, or GIF up to 5MB.
                  </p>
                  {(imagePreviewUrl || formData.imageUrl) && !removeCurrentImage ? (
                    <div className="mt-3 overflow-hidden rounded-lg border border-gray-200">
                      <img
                        src={imagePreviewUrl || formData.imageUrl}
                        alt={formData.name || 'Menu item preview'}
                        className="h-40 w-full object-contain object-center bg-gray-100"
                      />
                    </div>
                  ) : (
                    <div className="mt-3 flex h-40 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 text-center">
                      <span className="text-sm font-medium text-gray-500">
                        {formData.name || 'No image selected'}
                      </span>
                    </div>
                  )}
                  {(formData.imageUrl || selectedImageFile) && !removeCurrentImage ? (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedImageFile(null);
                        setImagePreviewUrl('');
                        setRemoveCurrentImage(true);
                      }}
                      className="mt-3 text-sm font-medium text-red-600 hover:text-red-700"
                    >
                      Remove image
                    </button>
                  ) : null}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Enter item description"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="available"
                    checked={formData.available}
                    onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
                    className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                  />
                  <label htmlFor="available" className="text-sm text-gray-700 cursor-pointer">
                    Available for sale
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingItem(null);
                    }}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-teal-500 text-white py-2 rounded-lg font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {editingItem ? 'Update Item' : 'Add Item'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
